import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ClipboardList, Plus, RefreshCw, Trash2, Check, AlertTriangle, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import {
  facebookLeadFormsService,
  LeadGenFormSummary,
  LeadGenFormQuestion,
  STANDARD_LEAD_QUESTIONS,
} from '@/services/facebook-lead-forms.service';

interface Props {
  /** Currently picked Meta form ID. */
  value: string;
  /** Emitted when user picks an existing form or creates a new one. */
  onChange: (formId: string, formName?: string) => void;
  disabled?: boolean;
}

const DEFAULT_QUESTIONS: LeadGenFormQuestion[] = [
  { type: 'FULL_NAME' },
  { type: 'EMAIL' },
  { type: 'PHONE' },
];

/**
 * Picker shown in the Ad creative panel when the campaign objective is
 * OUTCOME_LEADS. Lists existing Lead Gen forms attached to the connected
 * Page and lets the user create a new one inline.
 */
export const LeadFormPicker: React.FC<Props> = ({ value, onChange, disabled }) => {
  const [forms, setForms] = useState<LeadGenFormSummary[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      setForms(await facebookLeadFormsService.list());
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
      setForms([]);
    } finally { setRefreshing(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const selectedName = useMemo(() => forms?.find((f) => f.id === value)?.name, [forms, value]);

  return (
    <>
      <div className="rounded-xl border border-info/30 bg-info-soft px-4 py-3">
        <div className="mb-3 flex items-start gap-2">
          <ClipboardList size={16} className="mt-0.5 shrink-0 text-info" />
          <div>
            <div className="text-sm font-semibold text-text-primary">Lead Gen form</div>
            <p className="mt-0.5 text-2xs text-text-secondary">
              Required for OUTCOME_LEADS ads. Pick an existing form or create one — Meta hosts it,
              and submissions land in your Leads inbox via webhook.
            </p>
          </div>
        </div>

        {value && selectedName && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-success/30 bg-success-soft px-3 py-2 text-xs">
            <Check size={13} className="shrink-0 text-success" />
            <span className="font-medium text-text-primary">Using: {selectedName}</span>
            <span className="ml-auto font-mono text-2xs text-text-muted">{value}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Select
            value={value}
            onChange={(e) => {
              const id = e.target.value;
              const f = forms?.find((x) => x.id === id);
              onChange(id, f?.name);
            }}
            disabled={disabled || refreshing}
            className="!h-9 flex-1"
          >
            <option value="">{forms === null ? 'Loading…' : forms.length === 0 ? 'No forms yet — create one →' : 'Select an existing form…'}</option>
            {(forms ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} {f.status ? `· ${f.status}` : ''}{typeof f.leads_count === 'number' ? ` · ${f.leads_count} leads` : ''}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            variant="secondary"
            onClick={refresh}
            disabled={refreshing || disabled}
            iconLeft={<RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />}
            title="Refresh from Meta"
          >
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            disabled={disabled}
            iconLeft={<Plus size={12} />}
          >
            New
          </Button>
        </div>
      </div>

      <CreateLeadFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id, name) => {
          setCreateOpen(false);
          onChange(id, name);
          refresh();
        }}
      />
    </>
  );
};

// ─── Create modal ─────────────────────────────────────────────────────────────

interface CreateProps {
  open: boolean;
  onClose: () => void;
  onCreated: (formId: string, formName: string) => void;
}

const CreateLeadFormModal: React.FC<CreateProps> = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [thankYouBody, setThankYouBody] = useState('');
  const [questions, setQuestions] = useState<LeadGenFormQuestion[]>(DEFAULT_QUESTIONS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setPrivacyPolicyUrl('');
      setWebsiteUrl('');
      setThankYouBody('');
      setQuestions(DEFAULT_QUESTIONS);
    }
  }, [open]);

  const addStandard = (type: string) => {
    if (questions.find((q) => q.type === type)) return;
    setQuestions([...questions, { type }]);
  };

  const addCustom = () => {
    setQuestions([...questions, { type: 'CUSTOM', label: '' }]);
  };

  const removeQuestion = (idx: number) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const updateLabel = (idx: number, label: string) => {
    setQuestions(questions.map((q, i) => (i === idx ? { ...q, label } : q)));
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Form name is required'); return; }
    if (!privacyPolicyUrl.trim()) { toast.error('Privacy policy URL is required (Meta mandate)'); return; }
    if (questions.length === 0) { toast.error('Add at least one question'); return; }
    for (const q of questions) {
      if (q.type === 'CUSTOM' && !q.label?.trim()) {
        toast.error('Custom questions need a label');
        return;
      }
    }
    setBusy(true);
    try {
      const r = await facebookLeadFormsService.create({
        name: name.trim(),
        privacyPolicyUrl: privacyPolicyUrl.trim(),
        websiteUrl: websiteUrl.trim() || undefined,
        thankYouBody: thankYouBody.trim() || undefined,
        questions,
      });
      toast.success(`Form "${r.name}" created on Meta`);
      onCreated(r.id, r.name);
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusy(false); }
  };

  const usedTypes = new Set(questions.filter((q) => q.type !== 'CUSTOM').map((q) => q.type));
  const remainingStandard = STANDARD_LEAD_QUESTIONS.filter((q) => !usedTypes.has(q.type));

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="New Lead Gen form" widthClass="max-w-xl">
      <div className="space-y-1">
        <FormField label="Form name" required hint="Internal name. Visitors don't see this.">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Spring Sale - UAE Leads" />
        </FormField>

        <FormField label="Privacy policy URL" required hint="Public URL to your privacy policy. Meta requires this on every Lead Gen form.">
          <Input value={privacyPolicyUrl} onChange={(e) => setPrivacyPolicyUrl(e.target.value)} placeholder="https://yourwebsite.com/privacy" />
        </FormField>

        <FormField label="Website URL" hint="Shown on the thank-you screen. Optional.">
          <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://yourwebsite.com" />
        </FormField>

        <FormField label="Thank-you body" hint="What the user sees after submitting. Defaults to a generic message.">
          <Textarea value={thankYouBody} onChange={(e) => setThankYouBody(e.target.value)} rows={2} placeholder="Thanks! Someone will be in touch shortly." />
        </FormField>

        {/* Questions */}
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">
              Questions ({questions.length})
            </span>
          </div>

          <div className="space-y-1.5">
            {questions.map((q, idx) => (
              <div key={idx} className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
                <span className="rounded bg-surface-alt px-1.5 py-0.5 text-2xs font-mono uppercase text-text-secondary">
                  {q.type === 'CUSTOM' ? 'Custom' : q.type}
                </span>
                {q.type === 'CUSTOM' ? (
                  <Input
                    value={q.label ?? ''}
                    onChange={(e) => updateLabel(idx, e.target.value)}
                    placeholder="Question prompt"
                    className="!h-8 !text-xs flex-1"
                  />
                ) : (
                  <span className="flex-1 text-xs text-text-primary">
                    {STANDARD_LEAD_QUESTIONS.find((x) => x.type === q.type)?.label ?? q.type}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeQuestion(idx)}
                  className="rounded p-1 text-text-muted hover:bg-danger-soft hover:text-danger transition-colors"
                  title="Remove"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>

          {/* Add buttons */}
          <div className="mt-2 flex flex-wrap gap-1">
            {remainingStandard.map((q) => (
              <button
                key={q.type}
                type="button"
                onClick={() => addStandard(q.type)}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-medium text-text-secondary hover:border-border-strong hover:text-text-primary transition-colors"
              >
                <Plus size={10} /> {q.label}
              </button>
            ))}
            <button
              type="button"
              onClick={addCustom}
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-2xs font-medium transition-colors',
                'border border-primary/30 bg-primary-soft text-primary hover:bg-primary/10',
              )}
            >
              <Plus size={10} /> Custom question
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2">
          <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warning" />
          <p className="text-2xs text-warning">
            Once created on Meta, forms can be edited in <a href="https://business.facebook.com/" target="_blank" rel="noreferrer" className="underline">Business Manager <ExternalLink size={9} className="inline" /></a>. SlightorCRM doesn't support inline editing yet.
          </p>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Create on Meta</Button>
        </div>
      </div>
    </Modal>
  );
};
